/**
 * Where the visitor came from: ad clicks and campaign links, captured from the landing URL (and from the
 * `_keepo_source` cookie the marketing site leaves) and sent to the API at signup and on each dashboard load.
 *
 * Three touches are kept, because one alone loses the ad click:
 * - `first`: the first one ever seen in this browser;
 * - `last`: the most recent one (a returning user's click is what led to a purchase);
 * - `ads`: the most recent ad click (one with a click id). A "made with Keepo" footer link visited after
 *   an ad click updates `last` but never replaces `ads`, so the signup is still attributed to the ad.
 *
 * Stored in localStorage and mirrored in a first-party cookie (90 days, shared across keepo.bio
 * subdomains): browsers that clear one usually keep the other.
 */

const STORAGE_KEY = "Keepo.trafficOrigin";
const BACKUP_COOKIE_NAME = "_keepo_origin";
const BACKUP_COOKIE_DAYS = 90;
/** Left by the marketing site with the query string of the landing page. */
const SOURCE_COOKIE_NAME = "_keepo_source";
/** Google Analytics' cookie: "GA1.1.<client id>". */
const GA_COOKIE_NAME = "_ga";

const UTM_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_adset", "utm_content", "utm_term"] as const;

/** Ids an ad platform puts on the landing URL (gbraid/wbraid: Google's on iOS, where gclid is withheld). */
const CLICK_ID_PARAMS = [
  "gclid",
  "gbraid",
  "wbraid",
  "gad_source",
  "gad_campaignid",
  "fbclid",
  "msclkid",
  "ttclid",
] as const;

const TRACKING_PARAMS = [...UTM_PARAMS, ...CLICK_ID_PARAMS] as const;

type TrackingParam = (typeof TRACKING_PARAMS)[number];

export type TrafficTouch = Partial<Record<TrackingParam, string>> & {
  landing_page?: string;
  landing_origin?: string;
  captured_at?: string;
};

export interface TrafficOrigin {
  first?: TrafficTouch;
  last?: TrafficTouch;
  ads?: TrafficTouch;
  ga_client_id?: string;
}

type StoredTrafficOrigin = Pick<TrafficOrigin, "first" | "last" | "ads">;

const hasTrackingParams = (touch?: TrafficTouch): touch is TrafficTouch => {
  return !!touch && TRACKING_PARAMS.some(param => !!touch[param]);
};

const isAdClick = (touch: TrafficTouch) => CLICK_ID_PARAMS.some(param => !!touch[param]);

/** Same link as before (a reload, or the cookie and the URL saying the same): not a new touch. */
const isSameTouch = (a?: TrafficTouch, b?: TrafficTouch) => {
  return !!a && !!b && TRACKING_PARAMS.every(param => (a[param] ?? "") === (b[param] ?? ""));
};

const readCookie = (name: string): string | undefined => {
  const prefix = name + "=";
  for (const part of document.cookie.split(";")) {
    const entry = part.trim();
    if (entry.startsWith(prefix)) {
      return entry.slice(prefix.length);
    }
  }
  return undefined;
};

/** The current host and its parent domain (app.keepo.bio -> .keepo.bio), to reach cookies set on either. */
const cookieDomainCandidates = (): (string | undefined)[] => {
  const candidates: (string | undefined)[] = [undefined];
  const labels = window.location.hostname.split(".");
  if (labels.length >= 3) {
    candidates.push("." + labels.slice(1).join("."));
  }
  return candidates;
};

/** A param longer than this isn't a real id or campaign name: it's cut so the header sent to the API stays small. */
const MAX_PARAM_LENGTH = 300;

const touchFromParams = (params: URLSearchParams): TrafficTouch | undefined => {
  const touch: TrafficTouch = {};

  TRACKING_PARAMS.forEach(param => {
    const value = params.get(param)?.trim();
    if (value) {
      touch[param] = value.slice(0, MAX_PARAM_LENGTH);
    }
  });

  return hasTrackingParams(touch) ? touch : undefined;
};

/** The page without its query string: the params that matter are already in the touch. */
const landingPage = () => (window.location.origin + window.location.pathname).slice(0, MAX_PARAM_LENGTH);

/** A stored value in the current shape, or the single flat touch older versions of the app kept. */
const normalizeStored = (value: unknown): StoredTrafficOrigin | undefined => {
  if (!value || typeof value !== "object") {
    return undefined;
  }

  const candidate = value as StoredTrafficOrigin & TrafficTouch;
  if ("first" in candidate || "last" in candidate || "ads" in candidate) {
    const stored: StoredTrafficOrigin = {};
    (["first", "last", "ads"] as const).forEach(key => {
      if (hasTrackingParams(candidate[key])) {
        stored[key] = candidate[key];
      }
    });
    return stored.first || stored.last || stored.ads ? stored : undefined;
  }

  if (hasTrackingParams(candidate)) {
    return { first: candidate, last: candidate, ...(isAdClick(candidate) ? { ads: candidate } : {}) };
  }

  return undefined;
};

const readStored = (): StoredTrafficOrigin | undefined => {
  try {
    const fromStorage = normalizeStored(JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"));
    if (fromStorage) {
      return fromStorage;
    }
  } catch (e) {
    // Storage unavailable or unreadable: the cookie may still have it
  }

  try {
    const raw = readCookie(BACKUP_COOKIE_NAME);
    return raw ? normalizeStored(JSON.parse(decodeURIComponent(raw))) : undefined;
  } catch (e) {
    return undefined;
  }
};

/** Browsers drop a cookie over ~4KB (name, value and attributes): the copy is kept well under that. */
const MAX_COOKIE_VALUE_LENGTH = 3600;

const withoutLanding = (touch?: TrafficTouch): TrafficTouch | undefined => {
  if (!touch) {
    return undefined;
  }
  const rest = { ...touch };
  delete rest.landing_page;
  delete rest.landing_origin;
  return rest;
};

/**
 * The cookie's value: everything when it fits; otherwise without the landing addresses; otherwise only
 * what attribution can't do without (the ad click, or the first touch when there's no ad click).
 */
const cookieValueFor = (stored: StoredTrafficOrigin): string | undefined => {
  const lean: StoredTrafficOrigin = {
    first: withoutLanding(stored.first),
    last: withoutLanding(stored.last),
    ads: withoutLanding(stored.ads),
  };
  const essential: StoredTrafficOrigin = lean.ads ? { ads: lean.ads } : { first: lean.first };

  for (const candidate of [stored, lean, essential]) {
    const value = encodeURIComponent(JSON.stringify(candidate));
    if (value.length <= MAX_COOKIE_VALUE_LENGTH) {
      return value;
    }
  }
  return undefined;
};

const expireBackupCookie = () => {
  cookieDomainCandidates().forEach(domain => {
    const domainAttr = domain ? `; domain=${domain}` : "";
    document.cookie = `${BACKUP_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttr}`;
  });
};

const writeBackupCookie = (stored: StoredTrafficOrigin) => {
  // One copy only: an older one (another domain scope, or one too big to replace) would be read instead
  expireBackupCookie();

  const value = cookieValueFor(stored);
  if (!value) {
    return;
  }

  const expires = new Date(Date.now() + BACKUP_COOKIE_DAYS * 86400000).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  const attributes = `expires=${expires}; path=/; SameSite=Lax${secure}`;

  // The parent domain first (shared with the other keepo.bio subdomains). The browser refuses it when it's
  // a public suffix (e.g. ".com.br"): then the cookie is kept for this host only.
  for (const domain of cookieDomainCandidates().reverse()) {
    document.cookie = `${BACKUP_COOKIE_NAME}=${value}; ${attributes}${domain ? `; domain=${domain}` : ""}`;
    if (readCookie(BACKUP_COOKIE_NAME) === value) {
      return;
    }
  }
};

const writeStored = (stored: StoredTrafficOrigin) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch (e) {
    // Ignore storage errors: the cookie below is the fallback.
  }

  try {
    writeBackupCookie(stored);
  } catch (e) {
    // Ignore cookie errors.
  }
};

/** What identifies a touch (its params), to tell the ones the API already has. */
const SENT_KEY = "Keepo.trafficOriginSent";
const MAX_SENT_SIGNATURES = 10;

const signatureOf = (touch: TrafficTouch) => TRACKING_PARAMS.map(param => touch[param] ?? "").join("|");

const readSentSignatures = (): string[] => {
  try {
    const sent = JSON.parse(localStorage.getItem(SENT_KEY) || "[]");
    return Array.isArray(sent) ? sent.filter(entry => typeof entry === "string") : [];
  } catch (e) {
    return [];
  }
};

const recordTouch = (touch: TrafficTouch) => {
  const stored = readStored() ?? {};

  // The same link again keeps the moment it was first seen
  if (isSameTouch(stored.last, touch)) {
    return;
  }

  // Already sent to the API and its link is still in the address (a reload, the app rendering again, the
  // next person on this browser): it isn't a new touch
  if (readSentSignatures().includes(signatureOf(touch))) {
    return;
  }

  const recorded: TrafficTouch = { ...touch, captured_at: new Date().toISOString() };

  writeStored({
    first: stored.first ?? recorded,
    last: recorded,
    ads: isAdClick(recorded) ? recorded : stored.ads,
  });
};

export const captureTrafficOriginFromCookie = () => {
  try {
    const raw = readCookie(SOURCE_COOKIE_NAME);
    if (!raw) {
      return;
    }

    const params = new URLSearchParams(decodeURIComponent(raw));
    const touch = touchFromParams(params);
    if (!touch) {
      return;
    }

    const landingOrigin = params.get("landing_origin")?.trim();
    if (landingOrigin) {
      touch.landing_origin = landingOrigin;
    }
    touch.landing_page = landingPage();

    recordTouch(touch);
  } catch (e) {
    // Ignore cookie, storage, and URL parsing errors.
  }
};

export const captureTrafficOriginFromUrl = () => {
  try {
    const touch = touchFromParams(new URLSearchParams(window.location.search));
    if (!touch) {
      return;
    }

    touch.landing_page = landingPage();

    recordTouch(touch);
  } catch (e) {
    // Ignore storage and URL parsing errors.
  }
};

/** The Google Analytics client id of this browser ("123456789.1700000000"), when its cookie exists. */
export const getGaClientId = (): string | undefined => {
  try {
    const match = /^GA\d+\.\d+\.(.+)$/.exec(readCookie(GA_COOKIE_NAME) ?? "");
    return match && /^[\w.-]{3,64}$/.test(match[1]) ? match[1] : undefined;
  } catch (e) {
    return undefined;
  }
};

const GA_SYNCED_KEY = "Keepo.gaClientSynced";

/**
 * What to send to the API: the touches waiting to be sent, with the Analytics client id. A touch equal to
 * another isn't repeated (the API reads a missing `last` as the ad click or the first touch). With no
 * touches, the client id alone is sent once per browser, so purchases of who came without a campaign can
 * still be tied to their sessions. Nothing to send: undefined.
 */
export const getTrafficOrigin = (): TrafficOrigin | undefined => {
  const stored = readStored();
  const gaClientId = getGaClientId();

  if (!stored) {
    return gaClientId && !isGaClientSynced(gaClientId) ? { ga_client_id: gaClientId } : undefined;
  }

  const payload: TrafficOrigin = {};
  if (stored.ads) {
    payload.ads = stored.ads;
  }
  if (stored.first && stored.first !== stored.ads && !isSameMoment(stored.first, stored.ads)) {
    payload.first = stored.first;
  }
  if (stored.last && !isSameMoment(stored.last, stored.ads) && !isSameMoment(stored.last, stored.first)) {
    payload.last = stored.last;
  }
  if (gaClientId) {
    payload.ga_client_id = gaClientId;
  }

  return payload;
};

/** The same touch object recorded once (first, last and ads start as one). */
const isSameMoment = (a?: TrafficTouch, b?: TrafficTouch) => {
  return !!a && !!b && a.captured_at === b.captured_at && isSameTouch(a, b);
};

const isGaClientSynced = (gaClientId: string) => {
  try {
    return localStorage.getItem(GA_SYNCED_KEY) === gaClientId;
  } catch (e) {
    // Storage unavailable: better not to send it on every load
    return true;
  }
};

/**
 * The API has what was sent: those touches are cleared (and remembered, so the same link isn't recorded
 * again) and the Analytics client id isn't sent again. A touch captured while the request was in flight
 * stays, to be sent next.
 */
export const markTrafficOriginSent = (sent: TrafficOrigin) => {
  const sentTouches = [sent.first, sent.last, sent.ads].filter(hasTrackingParams);
  const wasSent = (touch?: TrafficTouch) => !!touch && sentTouches.some(sentTouch => isSameMoment(sentTouch, touch));

  const stored = readStored();
  const remaining: StoredTrafficOrigin = {};
  if (stored) {
    (["first", "last", "ads"] as const).forEach(key => {
      if (stored[key] && !wasSent(stored[key])) {
        remaining[key] = stored[key];
      }
    });
  }

  if (remaining.first || remaining.last || remaining.ads) {
    writeStored(remaining);
  } else {
    clearTrafficOrigin();
  }
  clearTrafficOriginCookie();

  try {
    if (sentTouches.length) {
      const signatures = [...readSentSignatures(), ...sentTouches.map(signatureOf)];
      localStorage.setItem(SENT_KEY, JSON.stringify([...new Set(signatures)].slice(-MAX_SENT_SIGNATURES)));
    }
    if (sent.ga_client_id) {
      localStorage.setItem(GA_SYNCED_KEY, sent.ga_client_id);
    }
  } catch (e) {
    // Ignore storage errors.
  }
};

/** Forgets the touches kept in this browser (both copies). */
export const clearTrafficOrigin = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Ignore storage errors.
  }

  try {
    expireBackupCookie();
  } catch (e) {
    // Ignore cookie errors.
  }
};

export const clearTrafficOriginCookie = () => {
  try {
    cookieDomainCandidates().forEach(domain => {
      const domainAttr = domain ? `; domain=${domain}` : "";
      document.cookie = `${SOURCE_COOKIE_NAME}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domainAttr}`;
    });
  } catch (e) {
    // Ignore cookie errors.
  }
};
